import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import {
  fetchStudyById,
  createStudy,
  updateStudy,
} from "../../utils/api";
import useOptions from "../../hooks/useOptions";
import { useSnackbar } from "../../components/snackbar/SnackbarProvider";

const maroon = "#500000";

const AddEditStudy = ({ open, onClose, studyId, onSaveSuccess }) => {

  const [irbStudy, setIrbStudy] = useState("");
  const [studyName, setStudyName] = useState("");
  const [shortName, setShortName] = useState("");
  const [kitTypes, setKitTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { openSnackbar } = useSnackbar();

  const {
    options: kitTypeOptions,
    loading: kitTypesLoading,
    error: kitTypesError,
  } = useOptions("kit-types");

  const isEditMode = !!studyId;

  // Load study data if in edit mode
  useEffect(() => {
    const loadStudyData = async () => {
      if (open) {
        setErrors({});
        
        if (studyId) {
          try {
            setLoading(true);
            const study = await fetchStudyById(studyId);
            if (study) {
              setIrbStudy(study.irbStudy || "");
              setStudyName(study.studyName || "");
              setShortName(study.studyShortName || "");
              setKitTypes(Array.isArray(study.kitTypes) ? study.kitTypes : []);
            }
          } catch (error) {
            console.error("Error loading study:", error);
            openSnackbar("Failed to load study data", "error");
          } finally {
            setLoading(false);
          }
        } else {
          setIrbStudy("");
          setStudyName("");
          setShortName("");
          setKitTypes([]);
        }
      }
    };

    loadStudyData();
  }, [studyId, open, openSnackbar]);

  const validate = () => {
    const newErrors = {};

    if (!irbStudy.trim()) {
      newErrors.irbStudy = "IRB Number is required";
    }

    if (!studyName.trim()) {
      newErrors.studyName = "Study Name is required";
    }

    if (!shortName.trim()) {
      newErrors.shortName = "Short Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const studyData = {
        irbStudy: irbStudy.trim(),
        studyName: studyName.trim(),
        studyShortName: shortName.trim(),
        kitTypes,
      };

      if (isEditMode) {
        await updateStudy(studyId, studyData);
        openSnackbar("Study updated successfully!", "success");
      } else {
        await createStudy(studyData);
        openSnackbar("Study created successfully!", "success");
      }

      onClose();
      if (onSaveSuccess) onSaveSuccess();
    } catch (error) {
      console.error("Error saving study:", error);
      openSnackbar(
        `Failed to ${isEditMode ? "update" : "create"} study. Please try again.`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    onClose();
  };



  const actions = [
    <Button
      key="cancel"
      variant="outlined"
      onClick={handleBack}
      disabled={loading}
      sx={{
        textTransform: "none",
        borderColor: maroon,
        color: maroon,
        "&:hover": {
          borderColor: maroon,
          backgroundColor: "rgba(80, 0, 0, 0.1)",
        },
      }}
    >
      CANCEL
    </Button>,
    <Button
      key="submit"
      variant="contained"
      onClick={handleSubmit}
      disabled={loading}
      sx={{
        backgroundColor: maroon,
        textTransform: "none",
        "&:hover": {
          backgroundColor: "#4a0000",
        },
        "&:disabled": {
          backgroundColor: "#ccc",
        },
      }}
    >
      {loading ? "SAVING..." : isEditMode ? "UPDATE STUDY" : "CREATE STUDY"}
    </Button>,
  ].filter(Boolean);

  return (
    <>
      <Dialog open={open} onClose={handleBack} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: maroon, color: "white" }}>
          {isEditMode ? "Edit Study" : "Add New Study"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mt: 2 }}>
        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* IRB Number Field */}
                <TextField
                  label="IRB Number"
                  value={irbStudy}
                  onChange={(e) => setIrbStudy(e.target.value)}
                  error={!!errors.irbStudy}
                  helperText={errors.irbStudy}
                  fullWidth
                  required
                  disabled={loading}
                />

                {/* Study Name Field */}
                <TextField
                  label="Study Name"
                  value={studyName}
                  onChange={(e) => setStudyName(e.target.value)}
                  error={!!errors.studyName}
                  helperText={errors.studyName}
                  fullWidth
                  required
                  disabled={loading}
                />

                {/* Short Name Field */}
                <TextField
                  label="Short Name"
                  value={shortName}
                  onChange={(e) => setShortName(e.target.value)}
                  error={!!errors.shortName}
                  helperText={errors.shortName}
                  fullWidth
                  required
                  disabled={loading}
                />

                <FormControl
                  fullWidth
                  error={!!errors.kitTypes}
                  disabled={loading || kitTypesLoading}
                >
                  <InputLabel>Kit Types</InputLabel>
                  <Select
                    multiple
                    value={kitTypes}
                    label="Kit Types"
                    onChange={(event) => setKitTypes(event.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {selected.map((value) => (
                          <Chip key={value} size="small" label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {kitTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label || option.value}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors.kitTypes || kitTypesError) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.75, ml: 1.75 }}
                    >
                      {errors.kitTypes || "Kit type options failed to load."}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {actions}
        </DialogActions>
      </Dialog>


    </>
  );
};

export default AddEditStudy;
